import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listInventoryMaps, listStorageLocations, setMapCalibration,
  type MapFrameTransform, type StorageLocationRow,
} from "../../api/rest";
import { useSiteId } from "../../hooks/useSiteId";
import {
  IDENTITY, applyTransform, boundsOf, correspondenceKey, fitView, isIdentity,
  normalizeTheta, panView, residuals, rmsOf, rotateAbout, scaleAbout, screenToWorld,
  toDegrees, toRadians, translateBy, unionBounds, worldToScreen, zoomViewAt,
  type Bounds, type Point, type Residual, type View,
} from "../mapCalibration";
import css from "./MapAlignTab.module.css";

/** Above this the auto-aligner calls a fit `poor_fit_needs_data_check`; the editor uses the
 *  same threshold so a manual calibration is held to the automatic one's standard. */
const MAX_GOOD_RMS_M = 1.0;

/** How many of the worst-paired addresses the diagnosis lists. Five is enough to see whether
 *  the error is concentrated (a handful of bad addresses in the data) or spread (a bad
 *  transform) — the two have completely different fixes. */
const WORST_SHOWN = 5;

type Polys = readonly (readonly Point[])[];

interface MapGeometry {
  polygons: Polys;
  /** Correspondence key → centroid, in the map's own frame. */
  centroids: ReadonlyMap<string, Point>;
}

function centroid(poly: readonly Point[]): Point {
  let x = 0, y = 0;
  for (const p of poly) { x += p[0]; y += p[1]; }
  return [x / poly.length, y / poly.length];
}

/** Group raw rows by map, keeping both the geometry to draw and the keys to score against. */
function geometryByMap(rows: readonly StorageLocationRow[]): Map<string, MapGeometry> {
  const out = new Map<string, { polygons: Point[][]; centroids: Map<string, Point> }>();
  for (const r of rows) {
    if (!r.mapId || !r.polygon || r.polygon.length === 0) continue;
    const poly = r.polygon.map((p) => [p[0], p[1]] as Point);
    let entry = out.get(r.mapId);
    if (!entry) { entry = { polygons: [], centroids: new Map() }; out.set(r.mapId, entry); }
    entry.polygons.push(poly);
    const key = correspondenceKey(r.address, r.aisleLabel);
    if (key !== null && !entry.centroids.has(key)) entry.centroids.set(key, centroid(poly));
  }
  return out;
}

function drawPolygons(
  ctx: CanvasRenderingContext2D, view: View, polygons: Polys,
  t: MapFrameTransform, stroke: string, lineWidth: number,
) {
  ctx.strokeStyle = stroke;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  for (const poly of polygons) {
    for (let i = 0; i < poly.length; i++) {
      const [wx, wy] = applyTransform(t, poly[i][0], poly[i][1]);
      const [px, py] = worldToScreen(view, wx, wy);
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.closePath();
  }
  // One stroke for every polygon: 3 000+ bins per JSL level make a per-shape stroke the
  // difference between a live drag and a slideshow.
  ctx.stroke();
}

/** ADR 323: one segment per paired address, from where this map puts it to where the
 *  reference does. A correct alignment draws segments of zero length, so the overlay
 *  disappears exactly when there is nothing to diagnose. One sweep, like the polygons. */
function drawResiduals(
  ctx: CanvasRenderingContext2D, view: View, rs: readonly Residual[],
  stroke: string, lineWidth: number,
) {
  if (rs.length === 0) return;
  ctx.strokeStyle = stroke;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  for (const r of rs) {
    const [ax, ay] = worldToScreen(view, r.from[0], r.from[1]);
    const [bx, by] = worldToScreen(view, r.to[0], r.to[1]);
    ctx.moveTo(ax, ay);
    ctx.lineTo(bx, by);
  }
  ctx.stroke();
}

/**
 * ADR 258 — the visual editor for `inventory_map.map_to_warehouse`, now the
 * Alinhamento sub-tab of Mapa (spec 2026-08-11). `WarehousePage` already
 * validated `:warehouseId` and renders the shared title/back link; this tab
 * keeps only `mapCalibration.subtitle`, which explains the editor itself.
 *
 * Imports self-align (ADR 244) and that pass is correct on the real data, so this is the
 * fallback for what it cannot do: a map with no correspondence partner, a fit the guards
 * rejected, or a result an operator can see is wrong. It draws the already-calibrated maps
 * as the reference and the edited map on top, and reports the same RMS the aligner reports —
 * without it this would be a way to make a calibration look right rather than be right.
 */
export function MapAlignTab() {
  const { t } = useTranslation();
  const siteId = useSiteId();
  const { warehouseId = "" } = useParams();
  const qc = useQueryClient();

  // Same key, same options as WarehousePage and AddressesSection: one cache
  // entry, one retry policy. A shared key with divergent options refetches on
  // every mount of the odd one out.
  const { data: allMaps = [] } = useQuery({
    queryKey: ["inventory-maps", siteId],
    queryFn: () => listInventoryMaps(siteId),
    retry: 0,
  });
  const maps = useMemo(
    () => allMaps.filter((m) => m.warehouseId === warehouseId),
    [allMaps, warehouseId],
  );

  const { data: rawRows = [], isLoading } = useQuery({
    queryKey: ["rawLocations", siteId, warehouseId],
    queryFn: () => listStorageLocations(siteId, warehouseId, { raw: true }),
    enabled: !!warehouseId,
  });
  const geometry = useMemo(() => geometryByMap(rawRows), [rawRows]);

  // Default to the first map that still renders in place — that is the one needing work.
  const [pickedMapId, setPickedMapId] = useState("");
  const mapId = maps.some((m) => m.id === pickedMapId)
    ? pickedMapId
    : (maps.find((m) => !m.calibrated) ?? maps[0])?.id ?? "";
  const editedMap = maps.find((m) => m.id === mapId);

  const savedTransform: MapFrameTransform = editedMap?.mapToWarehouse ?? IDENTITY;
  const [local, setLocal] = useState<MapFrameTransform>(IDENTITY);
  // Guarded adjust-during-render: re-seed once per (map, saved value) identity, the same
  // pattern ShiftsAdminPage uses instead of an effect.
  const [seededFrom, setSeededFrom] = useState<unknown>(null);
  const seedKey = `${mapId}|${JSON.stringify(savedTransform)}`;
  if (editedMap && seedKey !== seededFrom) {
    setSeededFrom(seedKey);
    setLocal(savedTransform);
  }

  // A callback ref, not useRef: the canvas is behind an `isLoading` branch, so on first
  // render it does not exist yet. A `[]`-deps effect would run once against null, never
  // re-run, and leave the size at 0 — a permanently blank canvas under a perfectly healthy
  // page. State is what makes mounting observable.
  const [canvasEl, setCanvasEl] = useState<HTMLCanvasElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const canvasRef = useCallback((node: HTMLCanvasElement | null) => {
    setCanvasEl(node);
    // Measure here rather than in the effect: this fires exactly when the node mounts, and
    // it keeps the first paint from depending on ResizeObserver's initial callback.
    const parent = node?.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    setSize({ width: Math.floor(rect.width), height: Math.floor(rect.height) });
  }, []);

  // Reference = every OTHER map, drawn with its own saved calibration.
  const reference = useMemo(() => {
    const layers: { polygons: Polys; transform: MapFrameTransform }[] = [];
    let refCentroids = new Map<string, Point>();
    let best = -1;
    for (const m of maps) {
      if (m.id === mapId) continue;
      const g = geometry.get(m.id);
      if (!g) continue;
      const transform = m.mapToWarehouse ?? IDENTITY;
      layers.push({ polygons: g.polygons, transform });
      // Score against the richest other map — the aligner picks its reference the same way.
      if (g.centroids.size > best) {
        best = g.centroids.size;
        refCentroids = new Map(
          [...g.centroids].map(([k, p]) => [k, applyTransform(transform, p[0], p[1])]),
        );
      }
    }
    return { layers, refCentroids };
  }, [maps, mapId, geometry]);

  const edited = geometry.get(mapId);

  const worldBounds: Bounds | null = useMemo(() => {
    let b: Bounds | null = null;
    for (const layer of reference.layers) {
      b = unionBounds(b, boundsOf(layer.polygons.map((p) =>
        p.map(([x, y]) => applyTransform(layer.transform, x, y)))));
    }
    if (edited) {
      b = unionBounds(b, boundsOf(edited.polygons.map((p) =>
        p.map(([x, y]) => applyTransform(local, x, y)))));
    }
    return b;
  }, [reference, edited, local]);

  // ADR 323 — the view is STATE, not a derivation of the geometry. Deriving it meant the
  // canvas re-fitted on every pointer move of a drag: the map you were dragging pushed the
  // bounds, the bounds rescaled the view, and the map slid out from under the cursor. It
  // also made zoom impossible by construction, since any adjustment was recomputed away.
  const fitted = useMemo(
    () => fitView(worldBounds, size.width, size.height),
    [worldBounds, size.width, size.height],
  );
  const [view, setView] = useState<View>(fitted);
  // Re-fit on a new map, a resize, or the arrival of the data — never on a drag. Same
  // guarded adjust-during-render pattern as the transform seed above.
  const [fitKey, setFitKey] = useState<string | null>(null);
  const nextFitKey = `${mapId}|${size.width}x${size.height}|${rawRows.length}`;
  if (nextFitKey !== fitKey) {
    setFitKey(nextFitKey);
    setView(fitted);
  }
  const refit = useCallback(() => setView(fitted), [fitted]);

  /** The pivot every rotate/scale turns about: the middle of what is on screen. */
  const pivot: Point = useMemo(() => (worldBounds
    ? [(worldBounds.minX + worldBounds.maxX) / 2, (worldBounds.minY + worldBounds.maxY) / 2]
    : [0, 0]), [worldBounds]);

  // One pairing, read two ways: the score, and the segments drawn under it (ADR 323).
  const pairs = useMemo(() => {
    if (!edited) return [] as Residual[];
    const placed = new Map<string, Point>(
      [...edited.centroids].map(([k, p]) => [k, applyTransform(local, p[0], p[1])]),
    );
    return residuals(placed, reference.refCentroids);
  }, [edited, local, reference.refCentroids]);
  const score = useMemo(() => rmsOf(pairs), [pairs]);
  const worst = useMemo(() => pairs.slice(0, WORST_SHOWN), [pairs]);
  const [showResiduals, setShowResiduals] = useState(true);

  useEffect(() => {
    const parent = canvasEl?.parentElement;
    if (!parent) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width: Math.floor(width), height: Math.floor(height) });
    });
    ro.observe(parent);
    return () => ro.disconnect();
  }, [canvasEl]);

  useEffect(() => {
    const el = canvasEl;
    if (!el || size.width === 0) return;
    const dpr = window.devicePixelRatio || 1;
    el.width = size.width * dpr;
    el.height = size.height * dpr;
    const ctx = el.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.width, size.height);

    const style = getComputedStyle(el);
    const refColor = style.getPropertyValue("--op-ink-300").trim() || "#c7ccd6";
    const editColor = style.getPropertyValue("--op-blue-500").trim() || "#1E5BD8";
    const okColor = style.getPropertyValue("--op-ink-500").trim() || "#6b7280";
    const badColor = style.getPropertyValue("--op-danger").trim() || "#DC2626";

    for (const layer of reference.layers) drawPolygons(ctx, view, layer.polygons, layer.transform, refColor, 1);
    if (edited) drawPolygons(ctx, view, edited.polygons, local, editColor, 1.5);
    // Residuals last so they sit above both maps; the ones past the aligner's own threshold
    // get a second, louder sweep rather than a per-segment colour (ADR 323).
    if (showResiduals) {
      drawResiduals(ctx, view, pairs, okColor, 1);
      drawResiduals(ctx, view, pairs.filter((r) => r.d > MAX_GOOD_RMS_M), badColor, 2);
    }
  }, [canvasEl, size, view, reference.layers, edited, local, pairs, showResiduals]);

  // Wheel zoom, anchored at the cursor. Registered by hand because React's onWheel is
  // passive: preventDefault there is a no-op, and the page scrolls out from under the
  // canvas while the operator is zooming it.
  useEffect(() => {
    const el = canvasEl;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const factor = Math.exp(-e.deltaY * 0.0015);
      setView((v) => zoomViewAt(v, factor, e.clientX - rect.left, e.clientY - rect.top));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [canvasEl]);

  // ---- gestures ---------------------------------------------------------------------

  // A drag either MOVES THE MAP (plain left button — the editor's whole purpose) or PANS THE
  // VIEW (middle button, or shift held). Two gestures on one surface rather than a mode
  // toggle: a mode is state the operator has to remember, and getting it wrong here writes a
  // calibration instead of scrolling (ADR 323).
  const dragRef = useRef<{ world: Point; screen: Point; pan: boolean } | null>(null);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const [sx, sy] = [e.clientX - rect.left, e.clientY - rect.top];
    dragRef.current = {
      world: screenToWorld(view, sx, sy),
      screen: [sx, sy],
      pan: e.button === 1 || e.shiftKey,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [view]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const [sx, sy] = [e.clientX - rect.left, e.clientY - rect.top];
    if (drag.pan) {
      setView((v) => panView(v, sx - drag.screen[0], sy - drag.screen[1]));
      // The world point under the cursor moves with the view, so only the screen anchor
      // carries over — re-deriving `world` from the stale view would double the pan.
      dragRef.current = { ...drag, screen: [sx, sy] };
      return;
    }
    const now = screenToWorld(view, sx, sy);
    setLocal((prev) => translateBy(prev, now[0] - drag.world[0], now[1] - drag.world[1]));
    dragRef.current = { ...drag, world: now, screen: [sx, sy] };
  }, [view]);

  const endDrag = useCallback(() => { dragRef.current = null; }, []);

  const nudgeRotation = (deg: number) =>
    setLocal((prev) => {
      const r = rotateAbout(prev, toRadians(deg), pivot);
      return { ...r, theta: normalizeTheta(r.theta) };
    });
  const nudgeScale = (factor: number) => setLocal((prev) => scaleAbout(prev, factor, pivot));

  const save = useMutation({
    mutationFn: () => setMapCalibration(siteId, mapId, isIdentity(local) ? null : local),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory-maps", siteId] });
      qc.invalidateQueries({ queryKey: ["rawLocations", siteId, warehouseId] });
    },
  });

  const dirty = JSON.stringify(local) !== JSON.stringify(savedTransform);
  const rmsLabel = Number.isNaN(score.rms)
    ? t("mapCalibration.rmsUnmeasured")
    : `${score.rms.toFixed(2).replace(".", ",")} m`;

  return (
    <div className={css.root}>
      <div className={css.header}>
        <div className={css.subtitle}>{t("mapCalibration.subtitle")}</div>
      </div>

      {maps.length === 0 ? (
        <div className={css.card}>{t("mapCalibration.noMaps")}</div>
      ) : (
        <div className={css.layout}>
          <aside className={css.side}>
            <div className={css.sideTitle}>{t("mapCalibration.maps")}</div>
            <ul className={css.mapList}>
              {maps.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    className={m.id === mapId ? `${css.mapBtn} ${css.mapBtnOn}` : css.mapBtn}
                    aria-current={m.id === mapId}
                    onClick={() => setPickedMapId(m.id)}
                  >
                    <span className={css.mapName}>{m.name}</span>
                    <span className={css.mapMeta}>
                      {t("mapCalibration.bins", { n: m.locationCount ?? 0 })}
                      {" · "}
                      {m.calibrated ? t("mapCalibration.calibrated") : t("mapCalibration.inPlace")}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <div className={css.main}>
            <div className={css.canvasWrap}>
              {isLoading ? (
                <div className={css.skeleton} />
              ) : (
                <canvas
                  ref={canvasRef}
                  className={css.canvas}
                  style={{ width: "100%", height: "100%" }}
                  role="img"
                  aria-label={t("mapCalibration.canvasAria", { name: editedMap?.name ?? "" })}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={endDrag}
                  onPointerCancel={endDrag}
                />
              )}
            </div>

            <div className={css.legend}>
              <span><i className={css.swatchRef} aria-hidden /> {t("mapCalibration.legendRef")}</span>
              <span><i className={css.swatchEdit} aria-hidden /> {t("mapCalibration.legendEdit", { name: editedMap?.name ?? "" })}</span>
              <span className={css.gestureHint}>{t("mapCalibration.gestureHint")}</span>
            </div>

            <div className={css.controls}>
              <div className={css.group} role="group" aria-label={t("mapCalibration.rotateAria")}>
                <span className={css.groupLabel}>{t("mapCalibration.rotate")}</span>
                <button type="button" className={css.btn} onClick={() => nudgeRotation(-90)}>−90°</button>
                <button type="button" className={css.btn} onClick={() => nudgeRotation(-1)}>−1°</button>
                <button type="button" className={css.btn} onClick={() => nudgeRotation(1)}>+1°</button>
                <button type="button" className={css.btn} onClick={() => nudgeRotation(90)}>+90°</button>
                {/* The real Flextronics levels 4 and 5 import y-negated (ADR 244), and a half
                    turn is exactly what corrects them — worth one click, not two. */}
                <button type="button" className={css.btn} onClick={() => nudgeRotation(180)}>180°</button>
              </div>

              <div className={css.group} role="group" aria-label={t("mapCalibration.scaleAria")}>
                <span className={css.groupLabel}>{t("mapCalibration.scale")}</span>
                <button type="button" className={css.btn} onClick={() => nudgeScale(1 / 1.01)}>−1%</button>
                <button type="button" className={css.btn} onClick={() => nudgeScale(1.01)}>+1%</button>
              </div>

              {/* ADR 323 — the VIEW, which changes nothing that gets saved. Without it the
                  canvas always fitted every polygon, and a 15 cm residual on a 47 m aisle
                  drawn 400 px wide is a third of a pixel. */}
              <div className={css.group} role="group" aria-label={t("mapCalibration.zoomAria")}>
                <span className={css.groupLabel}>{t("mapCalibration.zoom")}</span>
                <button
                  type="button" className={css.btn} aria-label={t("mapCalibration.zoomOut")}
                  onClick={() => setView((v) => zoomViewAt(v, 1 / 1.4, size.width / 2, size.height / 2))}
                >−</button>
                <button
                  type="button" className={css.btn} aria-label={t("mapCalibration.zoomIn")}
                  onClick={() => setView((v) => zoomViewAt(v, 1.4, size.width / 2, size.height / 2))}
                >+</button>
                <button type="button" className={css.btn} onClick={refit}>{t("mapCalibration.fitView")}</button>
              </div>

              <dl className={css.readout}>
                <div><dt>tx</dt><dd>{local.tx.toFixed(2).replace(".", ",")}</dd></div>
                <div><dt>ty</dt><dd>{local.ty.toFixed(2).replace(".", ",")}</dd></div>
                <div><dt>θ</dt><dd>{toDegrees(normalizeTheta(local.theta)).toFixed(1).replace(".", ",")}°</dd></div>
                <div><dt>{t("mapCalibration.scale")}</dt><dd>{local.scale.toFixed(3).replace(".", ",")}</dd></div>
              </dl>
            </div>

            {/* The number that says whether this is right, not just whether it looks right. */}
            <div className={score.pairs > 0 && score.rms > MAX_GOOD_RMS_M ? `${css.fit} ${css.fitBad}` : css.fit}>
              {score.pairs === 0
                ? t("mapCalibration.noCorrespondence")
                : t("mapCalibration.fit", { rms: rmsLabel, pairs: score.pairs })}
            </div>

            {/* ADR 323 — where the error is. An RMS spread evenly across every bay is a bad
                transform; the same RMS coming from three addresses is bad data, and the two
                have nothing to do with each other. */}
            {score.pairs > 0 && (
              <div className={css.residuals}>
                <label className={css.toggle}>
                  <input
                    type="checkbox"
                    checked={showResiduals}
                    onChange={(e) => setShowResiduals(e.target.checked)}
                  />
                  {t("mapCalibration.showResiduals")}
                </label>
                <ol className={css.worstList}>
                  {worst.map((r) => (
                    <li key={r.key} className={r.d > MAX_GOOD_RMS_M ? css.worstBad : undefined}>
                      <span className={css.worstKey}>{r.key.replace("|", " ")}</span>
                      <span className={css.worstD}>{r.d.toFixed(2).replace(".", ",")} m</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className={css.footer}>
              <button
                type="button" className={css.primary}
                disabled={!dirty || save.isPending || !mapId}
                onClick={() => save.mutate()}
              >
                {save.isPending ? t("common.saving") : t("common.save")}
              </button>
              <button
                type="button" className={css.btn}
                disabled={!dirty} onClick={() => setLocal(savedTransform)}
              >
                {t("mapCalibration.reset")}
              </button>
              <button
                type="button" className={css.btn}
                disabled={isIdentity(local)} onClick={() => setLocal(IDENTITY)}
              >
                {t("mapCalibration.clear")}
              </button>
              {save.isError && (
                <span className={css.error}>
                  {t("mapCalibration.saveError", { msg: (save.error as Error).message })}
                </span>
              )}
              {dirty && !save.isError && <span className={css.hint}>{t("mapCalibration.unsaved")}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
